import { resetChangeTripsSelection } from '@/features/manage/manageActions';
import {
  abandonExchange,
  deletePurchaseFlowBooking,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransAlert } from '@/i18n/trans/alert';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { GTM_EVENT, trackEcommerceEventV2 } from '@/utils/gtm';
import {
  getBookingAncillaries,
  mapBookedAncillaryToExtrasItem,
  mapBookingToGTMTripItems,
} from '@/utils/gtmPayloads';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Shared cancellation flows for an in-progress purchase. Both confirm a real
 * BE cancellation BEFORE clearing local state and then drop the user on
 * `/purchase` to start fresh — so redux/localStorage are never cleared while
 * the booking still lives on the backend.
 *
 * Consumed by the checkout Cancel button and the header "Purchase" link
 * (see `usePurchaseNavGuard`).
 */
export function useCancelInProgressPurchase() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setCurrentSearchFormValues } = useSearchWidget();
  const booking = useSelector(purchaseFlowBookingSelector);

  // Regular purchase: delete the booking on the BE, then reset local state.
  const cancelPurchaseBooking = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    try {
      await dispatch(deletePurchaseFlowBooking(booking.id)).unwrap();
      // Tagging plan (BR-57983): pushed only after the BE delete succeeds.
      trackEcommerceEventV2(GTM_EVENT.BOOKING_CANCELLED, {
        currency: booking.provisionalPrice?.currency,
        value: booking.provisionalPrice?.amount,
        total_passenger: booking.passengers?.length,
        round_trip: (booking.bookedTrips?.length ?? 0) > 1,
        items: mapBookingToGTMTripItems(booking),
        extras_items: getBookingAncillaries(booking).map(
          mapBookedAncillaryToExtrasItem
        ),
      });
      dispatch(resetPurchase());
      // Cancelling in the change-booking flow should fully exit it — otherwise
      // the user lands on /purchase with the change-trips selection still in
      // redux and immediately re-enters the exchange search.
      dispatch(resetChangeTripsSelection());
      setCurrentSearchFormValues(undefined);
      void persistor.purge();
      navigate('/purchase');
    } catch {
      toast.error(<TransAlert i18nKey="purchaseCancellationFailed" />);
    }
  }, [booking, dispatch, navigate, setCurrentSearchFormValues]);

  // Change-booking: cancel the started exchange operation on the BE (handled
  // inside `abandonExchange`, which also resets purchase + change-trips state),
  // then start a clean purchase.
  const abandonExchangeFlow = useCallback(async () => {
    await dispatch(abandonExchange());
    setCurrentSearchFormValues(undefined);
    void persistor.purge();
    navigate('/purchase');
  }, [dispatch, navigate, setCurrentSearchFormValues]);

  return { cancelPurchaseBooking, abandonExchangeFlow };
}
